import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath } from 'react-router-dom'

import { Purchaser } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useLazyGetOrganisationChildren from '@/modules/admin-center/hooks/useLazyGetOrganisationChildren'
import { OrganisationHierarchyInfoModal } from '@/modules/admin-center/organisation-hierarchy/OrganisationHierarchyInfoModal'
import Button from '@/modules/shared/components/button/Button'
import { Fallback } from '@/modules/shared/components/fallback/Fallback'
import { BooleanItem } from '@/modules/shared/components/table/table-column-items/shared/BooleanItem'
import useOrganisationId from '@/modules/shared/hooks/params/useOrganisationId'
import { useViewType } from '@/modules/shared/hooks/useViewType'
import { useWindowSize } from '@/modules/shared/hooks/useWindowSize'
import { ChevronDownIcon } from '@/modules/shared/icons/ChevronDownIcon'
import { classNames } from '@/modules/shared/utils/classNames'

interface NestedPurchaserRowProps {
  organisation: Purchaser
  level: number
  forceExpandAll?: boolean
}

export const NestedPurchaserRow = ({ organisation, level, forceExpandAll }: NestedPurchaserRowProps) => {
  const { t } = useTranslation()
  const organisationId = useOrganisationId()
  const selectedOrgId = Number(organisationId)
  const [isExpanded, setIsExpanded] = useState(level === 0)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const selectedOrg = selectedOrgId === organisation?.id
  const { isUtility } = useViewType()
  const showHighlighted = selectedOrg && !isUtility
  const { fetchOrganisationChildren, organisation: orgWithDescents } = useLazyGetOrganisationChildren({
    organisationId: organisation?.id,
  })
  const { twoXL } = useWindowSize()

  function toggleAccordion() {
    if (!isExpanded && !orgWithDescents) {
      fetchOrganisationChildren()
    }
    setIsExpanded(!isExpanded)
  }

  const marginLeft = level <= 6 ? `${level * 2}rem` : '12rem'
  const children = useMemo(
    () => orgWithDescents?.children?.unpaginated || organisation?.children?.unpaginated,
    [orgWithDescents]
  )

  const getButtonText = useMemo(() => {
    if (isUtility) {
      return t('general.manage', 'Manage')
    } else {
      if (selectedOrg) {
        return t('general.selected', 'Selected')
      } else {
        return t('general.select', 'Select')
      }
    }
  }, [selectedOrg])

  return (
    <Fragment key={organisation?.id}>
      <tr>
        <td className="px-0">
          <div
            className={classNames('mt-1 flex h-12 w-full items-center rounded-l border-b border-l border-t', {
              'border-primaryLight bg-primaryLighter': showHighlighted,
              'bg-gray-100': !showHighlighted,
            })}
            style={{ marginLeft }}
          >
            {(organisation?.children?.totalCount || 0) > 0 ? (
              <ChevronDownIcon
                className={classNames('ml-1 size-8 cursor-pointer rounded-full p-1 text-gray-500 transition', {
                  '-rotate-270 transform': isExpanded,
                  '-rotate-90 transform': !isExpanded,
                  'text-primary hover:bg-primaryLight': showHighlighted,
                  'hover:bg-gray-200': !showHighlighted,
                })}
                onClick={toggleAccordion}
                data-testid={`show-org-hierarchy-dropdown-${level}`}
              />
            ) : (
              <div className="ml-1 size-8 p-1" />
            )}
            <div
              className={classNames('ml-1 w-full break-words', {
                'font-bold': organisation?.legalEntity,
                'text-primary': showHighlighted,
              })}
            >
              {organisation?.name}
            </div>
          </div>
        </td>
        <td className="px-0">
          <div
            className={classNames(
              'mt-1 flex h-12 items-center justify-center border-b border-t bg-gray-100 py-2 text-sm',
              {
                'border-primaryLight bg-primaryLighter text-primary': showHighlighted,
                'bg-gray-100': !showHighlighted,
              }
            )}
          >
            {organisation?.id}
          </div>
        </td>
        <td className="px-0">
          <div
            className={classNames(
              'mt-1 flex h-12 items-center justify-center border-b border-t bg-gray-100 py-2 text-sm',
              {
                'border-primaryLight bg-primaryLighter text-primary': showHighlighted,
                'bg-gray-100': !showHighlighted,
              }
            )}
          >
            <BooleanItem value={!!organisation?.legalEntity} />
          </div>
        </td>
        {isUtility ? (
          <td className="px-0">
            <div
              className={classNames(
                'mt-1 flex h-12 items-center justify-center border-b border-t bg-gray-100 py-2 text-sm',
                {
                  'border-primaryLight bg-primaryLighter text-primary': showHighlighted,
                  'bg-gray-100': !showHighlighted,
                }
              )}
            >
              <BooleanItem value={!!organisation?.isVerified} />
            </div>
          </td>
        ) : (
          <>
            {twoXL && organisation?.isAPurchaser && (
              <>
                <td className="px-0">
                  <div
                    className={classNames(
                      'mt-1 flex h-12 items-center justify-center border-b border-t bg-gray-100 py-2 text-sm',
                      {
                        'border-primaryLight bg-primaryLighter text-primary': showHighlighted,
                        'bg-gray-100': !showHighlighted,
                      }
                    )}
                  >
                    <Fallback condition={!!organisation?.departmentCode}>{organisation?.departmentCode}</Fallback>
                  </div>
                </td>
                <td className="px-0">
                  <div
                    className={classNames(
                      'mt-1 flex h-12 items-center justify-center border-b border-t bg-gray-100 py-2 text-sm',
                      {
                        'border-primaryLight bg-primaryLighter text-primary': showHighlighted,
                        'bg-gray-100': !showHighlighted,
                      }
                    )}
                  >
                    <Fallback condition={!!organisation?.defaultAccountId}>{organisation?.defaultAccountId}</Fallback>
                  </div>
                </td>
              </>
            )}
          </>
        )}
        {organisation?.isAPurchaser && (
          <td className="px-0">
            <div
              className={classNames(
                'mt-1 flex h-12 items-center justify-center border-b border-t bg-gray-100 py-2 text-sm',
                {
                  'border-primaryLight bg-primaryLighter text-primary': showHighlighted,
                  'bg-gray-100': !showHighlighted,
                }
              )}
            >
              <Button
                className={classNames('flex min-w-20 items-center justify-center rounded-md px-3 py-1.5 text-xs', {
                  'bg-primary text-white': showHighlighted,
                  'bg-gray-200 hover:brightness-95': !showHighlighted,
                })}
                onClick={() => {
                  setShowInfoModal(true)
                }}
                data-testid="view-info-button"
              >
                {t('general.viewInfo', 'View Info')}
              </Button>
              <OrganisationHierarchyInfoModal
                showModal={showInfoModal}
                setShowModal={setShowInfoModal}
                organisationId={organisation?.id}
              />
            </div>
          </td>
        )}
        <td className="px-0">
          <div
            className={classNames(
              'mt-1 flex h-12 items-center justify-center rounded-r border-b border-l-0 border-r border-t bg-gray-100 px-0 py-2',
              { 'border-primaryLight bg-primaryLighter': showHighlighted, 'bg-gray-100': !showHighlighted }
            )}
          >
            <Link
              to={
                isUtility
                  ? generatePath('/utilities/organisations/:organisationId/general', {
                      organisationId: String(organisation?.id),
                    })
                  : `/settings/organisations/${organisation?.id}/general`
              }
              className={classNames(
                'flex min-w-20 items-center justify-center rounded-md px-3 py-1.5 text-xs transition',
                {
                  'bg-primary text-white': showHighlighted,
                  'bg-gray-200 hover:brightness-95': !showHighlighted,
                }
              )}
              data-testid="select-org-button"
            >
              {getButtonText}
            </Link>
          </div>
        </td>
      </tr>
      {isExpanded &&
        !!children &&
        children.map((childOrganisation) => (
          <NestedPurchaserRow
            key={childOrganisation.id}
            organisation={
              {
                ...childOrganisation,
                // If the top level organisation has new approval workflows turned on, pass it down the hierarchy so that the table always displays the column
                featureFlags: { newApprovalWorkflows: organisation?.featureFlags?.newApprovalWorkflows },
              } as Purchaser
            }
            level={level + 1}
            forceExpandAll={forceExpandAll}
          />
        ))}
    </Fragment>
  )
}
